import { useEffect, useState } from 'react';
import { useAuth } from '@/stores/auth-store';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Clock, RefreshCw } from 'lucide-react';

interface SessionTimeoutProps {
  warningMinutes?: number;
  timeoutMinutes?: number;
}

export function SessionTimeout({ warningMinutes = 5, timeoutMinutes = 30 }: SessionTimeoutProps) {
  const { isAuthenticated, logout, refreshToken, isTokenExpired, setTokens } = useAuth();
  const { toast } = useToast();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState<number>(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = () => {
      if (isTokenExpired()) {
        logout();
        toast({
          title: 'Session expired',
          description: 'Please log in again to continue.',
          variant: 'destructive',
        });
        return;
      }

      // Calculate remaining time until token expires
      const expiresAt = Date.now() + timeoutMinutes * 60 * 1000; // Mock expiration
      const timeLeft = expiresAt - Date.now();
      const minutesLeft = Math.floor(timeLeft / (1000 * 60));

      setRemainingTime(minutesLeft);

      // Show warning when close to expiration
      if (minutesLeft <= warningMinutes && minutesLeft > 0) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    };

    // Check immediately
    checkSession();

    // Set up interval to check every minute
    const interval = setInterval(checkSession, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, warningMinutes, timeoutMinutes, isTokenExpired, logout, toast]);

  const handleExtendSession = async () => {
    try {
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await authAPI.refreshToken(refreshToken);
      if (response.success && response.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data as any;
        setTokens(accessToken, newRefreshToken);
        setShowWarning(false);
        toast({
          title: 'Session extended',
          description: 'Your session has been extended successfully.',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to extend session',
        description: 'Please log in again.',
        variant: 'destructive',
      });
      logout();
    }
  };

  const handleLogout = () => {
    logout();
    setShowWarning(false);
  };

  if (!showWarning || !isAuthenticated) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="mx-4 w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
            <Clock className="h-6 w-6 text-orange-600" />
          </div>
          <CardTitle>Session Expiring Soon</CardTitle>
          <CardDescription>
            Your session will expire in {remainingTime} minute{remainingTime !== 1 ? 's' : ''}. 
            Would you like to extend your session?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col space-y-2">
            <Button onClick={handleExtendSession} className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Extend Session
            </Button>
            <Button variant="outline" onClick={handleLogout} className="w-full">
              Log Out
            </Button>
          </div>
          <div className="text-center text-xs text-muted-foreground">
            Session will auto-logout if no action is taken
          </div>
        </CardContent>
      </Card>
    </div>
  );
}